import { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { KYC_DOCUMENTS_TYPES } from '@shinetools/kyc-validation';

import {
  CompanyCreationDocumentType,
  OutstandingAgreementType,
} from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { useConsumeQueryParam } from 'common/bento/lib/useConsumeQueryParam';
import Agreement from 'common/bento/modules/Agreement/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { apolloClient } from 'common/graphql/clients';
import useCompanyContext from 'common/hooks/useCompanyContext';
import webView from 'common/webView';
import SuspenseQuery from 'components/SuspenseQuery';
import { logEvent } from 'features/Analytics/analytics';
import { useFourthlineSdkScript } from 'features/Onboarding/components/FourthlineSdk/hooks';
import useOverrideSetCurrentPage from 'features/Onboarding/hooks/useOverrideSetCurrentPage';
import BeneficiariesDocs from 'features/Onboarding/legacy/Pages/BeneficiariesDocs';
import BeneficiariesDocsIntro from 'features/Onboarding/legacy/Pages/BeneficiariesDocsIntro';
import { User } from 'features/Onboarding/legacy/types';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import OnboardingPageAdapter from '../../components/OnboardingPageAdapter';
import Beneficiaries from '../Beneficiaries';
import CapitalDepositDraftStatutes from '../CapitalDepositDraftStatutes';
import CapitalDepositProofOfFunds from '../CapitalDepositProofOfFunds/CapitalDepositProofOfFunds';
import CompanyFinancialInfo from '../CompanyFinancialInfo';
import CompanyPapers from '../CompanyPapers';
import ComplianceAndPersonalAddress from '../ComplianceAndPersonalAddress/ComplianceAndPersonalAddress';
import Identity from '../Identity';
import Mandate from '../Mandate';
import PricingPlan from '../PricingPlan/PricingPlan';
import ProofOfAddress from '../ProofOfAddress';
import Qes from '../Qes/Qes';
import ShineStartVerification from '../ShineStartVerification';
import Statutes from '../Statutes';
import { VerificationModuleDataDocument } from './graphql/queries/verificationModuleData.gql';
import getSelectedDocument from './lib/getSelectedDocument';
import getVerificationSteps from './lib/steps/getVerificationSteps';
import { VerificationStatus, VerificationStep } from './lib/steps/types';
import machineConfig, { model, State } from './machine';
import DocumentSignature from './pages/DocumentSignature';
import DocumentUpload from './pages/DocumentUpload';
import { GeneralInformationVerification } from './pages/GeneralInformationVerification';
import HandwrittenIdentityInfo from './pages/HandwrittenIdentityInfo';
import HandwrittenIdentityUpload from './pages/HandwrittenIdentityUpload';
import Home from './pages/Home';
import OnboardingHub from './pages/OnboardingHub';

interface VerificationProps {
  companyProfileId: string;
  // Whether the user is the main applicant, in the sense that they started the application
  // it can either be the account holder or a mandatee, but an invited account holder is not a main applicant
  // it has not the same meaning as the isPrimaryApplicant field of the companyUser (whatever meaning this field has)
  // see https://github.com/shinetools/companies-profiles-service/blob/5ef5b342748d4252d038e8b9421946fe236c02fa/api/helpers/formatCompanyUser.js#L22
  isMainApplicant: boolean;
  applicantType: ApplicantType;
  applicationType: ApplicationType;
  isShineStart?: boolean;
  isSasuCreation?: boolean;
  onAbort?: () => void;
}

const VerificationComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const { applicantType, applicationType, verificationData } = moduleData;
    const { company, companyUser } = verificationData.viewer;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const steps = getVerificationSteps(moduleData);
    const numberOfRemainingSteps = steps.filter(
      ({ status }) =>
        status !== VerificationStatus.Completed &&
        status !== VerificationStatus.Validated,
    ).length;

    const onStepClick = useCallback(
      (step: VerificationStep) => {
        send(step.metadata.event);

        logEvent({
          name: 'Onboarding Verification Step Clicked',
          properties: {
            numberOfRemainingSteps,
            status: step.status,
            step: step.id,
          },
          usePrefix: false,
        });
      },
      [send, numberOfRemainingSteps],
    );

    const onStepCompleted = useCallback(
      (step: { id: VerificationStep['id'] }) => {
        send(model.events.NEXT());

        logEvent({
          name: 'Onboarding Verification Step Completed',
          properties: {
            step: step.id,
          },
          usePrefix: false,
        });
      },
      [send],
    );

    const adaptedPageProps = {
      onPrev,
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: moduleData.verificationData.viewer.profile as unknown as User,
    };

    switch (stringifiedState) {
      case State.HOME:
        return applicationType === ApplicationType.Standard &&
          applicantType === ApplicantType.AccountHolder ? (
          <OnboardingHub
            moduleData={moduleData}
            onBeneficiariesClick={() => send(model.events.GO_BENEFICIARIES())}
            onGeneralInformationClick={() =>
              send(model.events.GO_GENERAL_INFORMATION_VERIFICATION())
            }
            onPrev={onPrev}
            onStepClick={onStepClick}
            onSubmitApplication={() => send(model.events.NEXT())}
          />
        ) : (
          <Home
            isBeneficiary={
              moduleData.applicantType === ApplicantType.InvitedBeneficiary
            }
            isLoading={state.matches(
              State.POST_COMPLIANCE_AND_PERSONAL_ADDRESS_FINISHED_PROFILE_SEND,
            )}
            moduleData={moduleData}
            onBeneficiariesClick={() => send(model.events.GO_BENEFICIARIES())}
            onPrev={onPrev}
            send={send}
          />
        );

      case State.COMPANY_FINANCIAL_INFO:
        return (
          <CompanyFinancialInfo
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.COMPANY_FINANCIAL_INFO })
            }
          />
        );

      case State.PROOF_OF_ADDRESS:
        return (
          <ProofOfAddress
            companyProfileId={company.companyProfileId}
            isShineStart={moduleData.isShineStart}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.PROOF_OF_ADDRESS })
            }
          />
        );

      case State.COMPLIANCE_AND_PERSONAL_ADDRESS:
        return (
          <ComplianceAndPersonalAddress
            applicantType={applicantType}
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.COMPLIANCE_AND_PERSONAL_ADDRESS })
            }
          />
        );

      case State.IDENTITY:
        return (
          <Identity
            companyProfileId={company.companyProfileId}
            companyUserId={companyUser.companyUserId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.IDENTITY })
            }
          />
        );

      case State.QES:
        return (
          <Qes
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.QES })
            }
          />
        );

      case State.COMPANY_PAPERS:
        return (
          <CompanyPapers
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.COMPANY_PAPERS })
            }
          />
        );

      case State.PROOF_OF_FUNDS:
        return (
          <CapitalDepositProofOfFunds
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.PROOF_OF_FUNDS })
            }
          />
        );

      case State.STATUTES:
        return (
          <Statutes
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.STATUTES })
            }
          />
        );

      case State.DRAFT_STATUTES:
        return (
          <CapitalDepositDraftStatutes
            companyProfileId={company.companyProfileId}
            isMandatee={moduleData.applicantType === ApplicantType.Mandatee}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.DRAFT_STATUTES })
            }
          />
        );

      case State.MANDATE:
        return (
          <Mandate
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.MANDATE })
            }
          />
        );

      case State.BENEFICIARIES_INTRO:
        return (
          <OnboardingPageAdapter
            LegacyOnboardingPage={BeneficiariesDocsIntro}
            {...adaptedPageProps}
            setCurrentPage={() => send(model.events.NEXT())}
          />
        );

      case State.BENEFICIARIES_LIST:
        return (
          <OnboardingPageAdapter
            LegacyOnboardingPage={BeneficiariesDocs}
            {...adaptedPageProps}
            setCurrentPage={() => send(model.events.NEXT())}
          />
        );

      case State.SHINE_START_VERIFICATION:
        return (
          <ShineStartVerification
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.SHINE_START_VERIFICATION })
            }
          />
        );

      case State.HANDWRITTEN_IDENTITY_INFO: {
        return (
          <HandwrittenIdentityInfo
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.HANDWRITTEN_IDENTITY_UPLOAD: {
        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentType:
            CompanyCreationDocumentType.ProofOfIdentityHandWritten,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <HandwrittenIdentityUpload
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      // TODO(LED-3455): link to review pages
      case State.COMPANY_CERTIFICATE_OF_RESIDENCE: {
        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentType:
            CompanyCreationDocumentType.CompanyCertificateOfResidence,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <DocumentUpload
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.NO_CRIMINAL_CONVICTION_STATEMENT: {
        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentType:
            CompanyCreationDocumentType.NoCriminalConvictionStatement,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <DocumentUpload
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.POWER_OF_ATTORNEY: {
        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentType: CompanyCreationDocumentType.PowerOfAttorney,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <DocumentSignature
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.BENEFICIARIES_REGISTER: {
        if (
          applicantType !== ApplicantType.AccountHolder &&
          applicantType !== ApplicantType.Mandatee
        ) {
          throw new Error(
            `Invalid applicant type for the Beneficiaries module: ${applicantType}`,
          );
        }

        if (
          applicationType !== ApplicationType.Standard &&
          applicationType !== ApplicationType.CapitalDeposit
        ) {
          throw new Error(
            `Invalid application type for the Beneficiaries module: ${applicationType}`,
          );
        }

        return (
          <Beneficiaries
            applicantType={applicantType}
            applicationType={applicationType}
            companyProfileId={company.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.BENEFICIARIES_REGISTER })
            }
          />
        );
      }

      case State.PRICING_PLAN: {
        return (
          <PricingPlan
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.PRICING_PLAN })
            }
            packageType={null}
          />
        );
      }

      case State.AGREEMENT: {
        return (
          <Agreement
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : onStepCompleted({ id: State.AGREEMENT })
            }
            type={OutstandingAgreementType.Cgu}
          />
        );
      }

      case State.GENERAL_INFORMATION_VERIFICATION: {
        return (
          <GeneralInformationVerification
            moduleData={moduleData}
            onAbort={() => send(model.events.ABORT())}
            onPrev={onPrev}
          />
        );
      }

      case State.INIT:
      case State.POST_COMPLIANCE_AND_PERSONAL_ADDRESS_FINISHED_PROFILE_SEND:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  { devTools: true, machineConfig },
);

const Verification = asBentoModule<VerificationProps>(
  ({
    applicantType,
    applicationType,
    companyProfileId,
    isMainApplicant,
    isSasuCreation = false,
    isShineStart = false,
    onDone,
  }) => {
    const verificationQuery = useQuery(VerificationModuleDataDocument, {
      variables: {
        companyProfileId,
        includeKybDocuments: isMainApplicant,
        includeLegalBeneficiaries:
          applicantType === ApplicantType.AccountHolder || isMainApplicant,
        kycDocumentTypes: KYC_DOCUMENTS_TYPES,
      },
    });

    useEffect(() => {
      return webView.listenNativeMessage((message) => {
        if (message.action === 'NATIVE_ID_CHECK_COMPLETED') {
          apolloClient.refetchQueries({
            include: [VerificationModuleDataDocument],
          });
        }
      });
    }, []);

    // Pre-loading of the fourthline SDK library.
    // We don't remove it when unmounting to have it ready anytime during this session.
    useFourthlineSdkScript();

    const companyContext = useCompanyContext();

    const bootstrap = useConsumeQueryParam(`bootstrap:${machineConfig.id}`);

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={verificationQuery}
      >
        {(data) => (
          <VerificationComponent
            moduleData={{
              applicantType,
              applicationType,
              bootstrap,
              companyContext,
              isMainApplicant,
              isSasuCreation,
              isShineStart,
              verificationData: data,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

export default Verification;
